// Thin fetch wrapper. Pulls a Clerk session token before every call so the
// server's clerkMiddleware() can read it. Caller passes a tokenGetter from
// Clerk's `useAuth().getToken` so we never reach into hooks from outside React.

import type { ShipFromLocation } from '@shared/ship-from';
import type { RfqDocumentKind } from '@shared/rfq-documents';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

export type TokenGetter = () => Promise<string | null>;

export async function api<T>(
  path: string,
  init: (RequestInit & { tokenGetter: TokenGetter; idempotencyKey?: string }),
): Promise<T> {
  const { tokenGetter, idempotencyKey, headers, ...rest } = init;
  const token = await tokenGetter();

  const res = await fetch(path, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...(headers ?? {}),
    },
  });

  const text = await res.text();
  // The body isn't guaranteed to be JSON: a gateway/edge proxy can return a
  // plaintext 502/504 ("upstream error", "Bad Gateway", etc.) when our server
  // is restarting or a long request (e.g. PDF parse) outlives the proxy's
  // timeout. Parsing such a body blindly threw "Unexpected token 'u'…" and
  // masked the real status. Parse defensively and fall back to the raw text.
  let payload: any = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null; // non-JSON body (proxy error page / plaintext)
    }
  }

  if (!res.ok) {
    const isGateway = res.status === 502 || res.status === 503 || res.status === 504;
    const err: ApiError = {
      status: res.status,
      code: payload?.error?.code ?? (isGateway ? 'gateway_unavailable' : 'http_error'),
      message:
        payload?.error?.message ??
        (isGateway
          ? 'Service is busy or briefly unavailable — please try again.'
          : (text || res.statusText)),
      details: payload?.error?.details,
    };
    throw err;
  }
  return payload as T;
}

// =====================================================================
// Response shapes
// =====================================================================

export type LineStage =
  | 'received' | 'sourcing' | 'quoted' | 'accepted' | 'po_sent'
  | 'invoiced' | 'paid' | 'shipped' | 'delivered' | 'reviewed'
  | 'no_quote' | 'lost';

export const STAGE_VALUES: LineStage[] = [
  'received', 'sourcing', 'quoted', 'accepted', 'po_sent',
  'invoiced', 'paid', 'shipped', 'delivered', 'reviewed',
  'no_quote', 'lost',
];

export interface RfqListItem {
  id: string;
  ref: string;
  isAog: boolean;
  isUrgent: boolean;
  isFlagged: boolean;
  flagNote: string | null;
  /**
   * Manual position on the flagged priority board — ascending, smallest first.
   * null = flagged but never positioned (sorts last). See shared/rank.ts.
   */
  boardRank: number | null;
  /** 'manual' = broker-set (appears on the priority board); 'auto' = intake
   *  duplicate detection or a customer reply adding lines (badge only). */
  flagSource: 'manual' | 'auto' | null;
  quoteSent: boolean;
  quoteSentAt: string | null;
  receivedAt: string;
  sourceChannel: string | null;
  defaultCurrency: string | null;
  defaultIncoterms: string | null;
  parserConfidence: number | null;
  /** Customer's PO number, if a PO has been received. Step 3 first-class event. */
  customerPoNumber: string | null;
  /** When the customer's PO was received (auto-set on first capture, or explicit). */
  customerPoReceivedAt: string | null;
  customer: {
    id: string;
    displayName: string;
    emailDomain: string | null;
  } | null;
  contact: {
    id: string;
    fullName: string | null;
    email: string;
  } | null;
  lineCount: number;
  stageCounts: Array<{ stage: LineStage; count: number }>;
  /** Outbound channels this RFQ has been posted to (PartsBase, ILS, ...) */
  sourcingChannels: SourcingChannel[];
  matchingLines: Array<{
    id: string;
    lineNumber: number;
    partNumber: string;
    altPartNumber: string | null;
    stage: LineStage;
  }>;
}

export type SourcingChannel = 'partsbase' | 'ils' | 'stockmarket' | 'email_blast' | 'website';

export interface SourcingPost {
  id: string;
  channel: SourcingChannel;
  source: 'manual' | 'api';
  externalRef: string | null;
  notes: string | null;
  postedAt: string;
}

export interface CreateSourcingPostResponse {
  sourcingPost: SourcingPost;
  /** Number of `received` lines that auto-advanced to `sourcing`. */
  stagesAdvanced: number;
}

/**
 * GET /api/rfqs/accepted-awaiting-action — the customer said yes and nobody has
 * acted yet. A focus list, not a feed: capped server-side and unpaginated,
 * because the RFQ list's keyset cursor cannot carry hoisted rows.
 */
export interface AcceptedAwaitingAction {
  quoteId: string;
  quoteRef: string;
  acceptedAt: string | null;
  customerTotalCents: number;
  currency: string;
  isExchange: boolean;
  coreChargeCents: number | null;
  rfqId: string;
  rfqRef: string;
  isAog: boolean;
  lineId: string;
  partNumber: string;
  qty: number;
  customerId: string | null;
  customerLegalName: string | null;
  contactFullName: string | null;
  contactEmail: string | null;
}

export interface AcceptedAwaitingActionResponse {
  acceptances: AcceptedAwaitingAction[];
  truncated: boolean;
}

/**
 * GET /api/rfqs/awaiting-reply — buyers who wrote and have not heard back, oldest
 * first. Driven by rfqs.awaiting_reply_since, not the flag.
 */
export interface BuyerAwaitingReply {
  rfqId: string;
  rfqRef: string;
  isAog: boolean;
  awaitingSince: string | null;
  customerLegalName: string | null;
  contactFullName: string | null;
  contactEmail: string | null;
  lastMessageExcerpt: string | null;
}

export interface BuyersAwaitingReplyResponse {
  buyers: BuyerAwaitingReply[];
  truncated: boolean;
}

/** POST /api/rfqs/:publicId/mark-answered */
export interface MarkAnsweredResponse {
  rfqId: string;
  changed: boolean;
}

/** POST /api/customer-quotes/:publicId/acknowledge-acceptance */
export interface AcknowledgeAcceptanceResponse {
  quoteId: string;
  acknowledgedAt: string;
  alreadyAcknowledged: boolean;
}

export interface RfqListResponse {
  rfqs: RfqListItem[];
  nextCursor: string | null;
}

export interface RfqDetailResponse {
  rfq: {
    id: string;
    ref: string;
    isAog: boolean;
    isUrgent: boolean;
    isFlagged: boolean;
    flagNote: string | null;
    /** 'manual' = broker-set (on the priority board); 'auto' = intake duplicate
     *  detection or a customer reply adding lines (badge only, off the board). */
    flagSource: 'manual' | 'auto' | null;
    boardRank: number | null;
    quoteSent: boolean;
    quoteSentAt: string | null;
    defaultCurrency: string | null;
    defaultIncoterms: string | null;
    sourceChannel: string | null;
    sourceRawText: string | null;
    parserModel: string | null;
    parserConfidence: number | null;
    notes: string | null;
    receivedAt: string;
    closedAt: string | null;
    // ---- Customer PO (Step 3 — first-class event captured before proforma) ----
    customerPoNumber: string | null;
    customerPoReceivedAt: string | null;
    customerPoDocumentKey: string | null;
    customerPoDocumentUrl: string | null;
    customerPoDocumentFilename: string | null;
    customerPoDocumentSha256: string | null;
    customerPoNotes: string | null;
  };
  sourcingPosts: SourcingPost[];
  customer: {
    id: string;
    legalName: string;
    displayName: string | null;
    emailDomain: string | null;
    countryCode: string | null;
    // BIS/OFAC screen status (Step 4 Phase A) — null = never screened.
    bisScreenStatus: BisScreenStatus | null;
    bisLastScreenedAt: string | null;
    bisScreenNotes: string | null;
    bisScreenPdfKey: string | null;
    bisScreenPdfUrl: string | null;
    bisScreenDocumentFilename: string | null;
    bisScreenDocumentSha256: string | null;
  } | null;
  contact: {
    id: string;
    email: string;
    fullName: string | null;
    phone: string | null;
    jobTitle: string | null;
  } | null;
  lineItems: Array<{
    id: string;
    lineNumber: number;
    partNumber: string;
    altPartNumber: string | null;
    description: string | null;
    manufacturer: string | null;
    aircraftPlatform: string | null;
    qty: number;
    uom: string;
    conditionRequested: string | null;
    conditionsAccepted: string[] | null;
    certRequested: string | null;
    isAog: boolean;
    stage: LineStage;
    stageChangedAt: string;
    eccn: string | null;
    htsCode: string | null;
    countryOfOrigin: string | null;
    hazmatClass: string | null;
    itarControlled: boolean;
    traceabilityRequired: boolean;
    traceabilityProvided: boolean;
    airworthinessCertKey: string | null;
    airworthinessCertUrl: string | null;
    airworthinessCertFilename: string | null;
    airworthinessCertSha256: string | null;
    airworthinessCertUploadedAt: string | null;
    notes: string | null;
  }>;
}

/** POST /api/rfqs/:publicId/customer-po-document — upload customer PO PDF/image. */
export interface UploadCustomerPoDocumentRequest {
  fileBase64: string;
  fileName: string;
  mimeType: string;
}
export interface UploadCustomerPoDocumentResponse {
  rfq: {
    id: string;
    customerPoDocumentKey: string | null;
    customerPoDocumentUrl: string | null;
    customerPoDocumentFilename: string | null;
    customerPoDocumentSha256: string | null;
    customerPoReceivedAt: string | null;
  };
}

// =====================================================================
// Customer BIS/OFAC screening (Step 4)
// =====================================================================

/** Status values the broker can record. `null` on a customer means never screened. */
export type BisScreenStatus = 'clear' | 'flagged' | 'review_pending';

/** POST /api/customers/:publicId/bis-screen — record a manual CSL/SDN screen. */
export interface RecordBisScreenRequest {
  status: BisScreenStatus;
  notes?: string;
}
export interface RecordBisScreenResponse {
  customer: {
    id: string;
    legalName: string;
    countryCode: string | null;
    bisScreenStatus: BisScreenStatus | null;
    bisLastScreenedAt: string | null;
    bisScreenNotes: string | null;
    bisScreenPdfKey: string | null;
    bisScreenPdfUrl: string | null;
    bisScreenDocumentFilename: string | null;
    bisScreenDocumentSha256: string | null;
  };
}

/** POST /api/customers/:publicId/bis-screen-document — upload CSL search result PDF/image. */
export interface UploadBisScreenDocumentRequest {
  fileBase64: string;
  fileName: string;
  mimeType: string;
}
export interface UploadBisScreenDocumentResponse {
  customer: {
    id: string;
    bisScreenPdfKey: string | null;
    bisScreenPdfUrl: string | null;
    bisScreenDocumentFilename: string | null;
    bisScreenDocumentSha256: string | null;
  };
}

// =====================================================================
// Vendor quotes
// =====================================================================

export type ConditionCode = 'NE' | 'NS' | 'FN' | 'OH' | 'SV' | 'AR' | 'RP';
export type CertType = 'faa_8130_3' | 'easa_form_1' | 'dual_release' | 'coc' | 'none';
export type Incoterms2020 = 'EXW' | 'FCA' | 'CPT' | 'CIP' | 'DAP' | 'DPU' | 'DDP' | 'FAS' | 'FOB' | 'CFR' | 'CIF';
export type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'JPY' | 'CHF' | 'AED' | 'SGD';

export interface VendorQuoteCandidateLine {
  id: string;
  rfqId: string;
  rfqRef: string;
  rfqIsAog: boolean;
  lineNumber: number;
  partNumber: string;
  altPartNumber: string | null;
  qty: number;
  conditionRequested: string | null;
  stage: LineStage;
}

export interface ParsedVendorQuoteDraft {
  vendor: { name: string; contact_name?: string; contact_email?: string };
  partNumber: string;
  altPartNumber: string | null;
  qtyOffered: number;
  unitPrice: number;
  unitPriceCents: number;
  currency: Currency;
  conditionOffered: ConditionCode | null;
  certOffered: CertType | null;
  leadTimeDays: number | null;
  /** Exchange terms as the parser read them. The core is the vendor's figure. */
  isExchange: boolean;
  coreChargeCents: number | null;
  /** ISO date, when the vendor gave an absolute deadline. */
  coreDue: string | null;
  /** Relative window, when the vendor gave days instead of a date. */
  coreReturnDays: number | null;
  incoterms: Incoterms2020 | null;
  fobLocation: string | null;
  validUntil: string | null;
  eccn: string | null;
  htsCode: string | null;
  countryOfOrigin: string | null;
  hazmatClass: string | null;
  notes: string | null;
}

export interface ParseVendorQuoteResponse {
  parsed: ParsedVendorQuoteDraft;
  parser: {
    model: string;
    confidence: number;
    warnings: string[];
    durationMs: number;
    inputType: 'paste' | 'pdf';
  };
  candidateLines: VendorQuoteCandidateLine[];
}

export interface VendorQuote {
  id: string;
  rfqLineItemId: string;
  rfqId?: string;
  rfqRef?: string;
  lineNumber?: number;
  vendorNameRaw: string;
  vendorContactName: string | null;
  vendorContactEmail: string | null;
  unitPriceCents: number;
  currency: Currency;
  qtyOffered: number;
  conditionOffered: ConditionCode | null;
  certOffered: CertType | null;
  leadTimeDays: number | null;
  /** Exchange terms — structured since 2026-08-25. Older parsed quotes carry
   *  the core only in `notes` text; the quote modal regex-prefills from there. */
  isExchange?: boolean;
  coreChargeCents?: number | null;
  coreDueAt?: string | null;
  coreReturnDays?: number | null;
  incoterms: Incoterms2020 | null;
  fobLocation: string | null;
  validUntil: string | null;
  eccn?: string | null;
  htsCode?: string | null;
  countryOfOrigin?: string | null;
  hazmatClass?: string | null;
  source: 'manual' | 'ai_parsed_email' | 'ai_parsed_pdf';
  parserConfidence: number | null;
  parserModel: string | null;
  rawSourceKey: string | null;
  rawSourceUrl: string | null;
  rawSourceMimeType: string | null;
  reviewStatus: 'pending' | 'confirmed' | 'rejected';
  notes: string | null;
  createdAt: string;
  /** Customer quote refs generated from this vendor quote. Non-empty means this
   *  is the vendor the customer was quoted from. */
  quotedOnRefs?: string[];
  /** Vendor PO refs raised against it. */
  poRefs?: string[];
  /** When it was FIRST used on a customer quote. */
  quotedAt?: string | null;
}

export interface CommitVendorQuoteResponse {
  vendorQuote: VendorQuote;
  lineStageAdvanced: boolean;
  newLineStage: LineStage;
  /** Non-fatal warnings from the commit (e.g. PDF couldn't be archived). */
  warnings?: string[];
}

/** A document already generated from a vendor quote. `refreshable` is true only
 *  for drafts: everything else is a frozen historical snapshot and keeps the
 *  values it was issued with. */
export interface DownstreamDoc {
  kind: 'vendor_po' | 'customer_quote';
  publicId: string;
  ref: string;
  status: string;
  refreshable: boolean;
}

export interface VendorQuoteImpactResponse {
  publicId: string;
  downstream: DownstreamDoc[];
  frozenCount: number;
  refreshableCount: number;
}

export interface PatchVendorQuoteResponse {
  publicId: string;
  /** Field -> {from, to}. Empty when nothing actually differed. */
  changed: Record<string, { from: unknown; to: unknown }>;
  downstream: DownstreamDoc[];
  unchanged: boolean;
}

export interface VendorQuoteListResponse {
  vendorQuotes: VendorQuote[];
}

// =====================================================================
// Customer quote generation
// =====================================================================

export type ClientQuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';

export interface GenerateCustomerQuoteResponse {
  quoteRef: string;
  rfqRef: string;
  generatedAt: string;
  validUntil: string;
  currency: Currency;
  breakdown: {
    vendorUnitCents: number;
    vendorTotalCents: number;
    markupPct: number;
    customerUnitCents: number;
    customerTotalCents: number;
    marginCents: number;
    marginPct: number;
  };
  markupSource: 'tenant' | 'customer' | 'line' | 'request';
  /** Full HTML document, ready to render in an iframe / download / copy. */
  html: string;
}

export interface ClientQuote {
  id: string;
  ref: string;
  status: ClientQuoteStatus;
  rfqRef: string;
  rfqLineItemId: string;
  lineNumber: number;
  qty: number;
  currency: Currency;
  vendorUnitPriceCents: number;
  customerUnitPriceCents: number;
  customerTotalCents: number;
  markupPct: number;
  markupSource: 'tenant' | 'customer' | 'line' | 'request';
  validUntil: string;
  generatedAt: string;
  sentAt: string | null;
  acceptedAt: string | null;
  /** When a human confirmed they SAW the acceptance. Null on an accepted quote
   *  means it is still pinned to the attention list. */
  acceptanceAcknowledgedAt: string | null;
  rejectedAt: string | null;
  expiredAt: string | null;
  notes: string | null;
  /** Present on detail responses; omitted from list responses to keep them lightweight. */
  html?: string;
}

export interface CommitCustomerQuoteResponse {
  clientQuote: ClientQuote;
  breakdown: GenerateCustomerQuoteResponse['breakdown'];
  markupSource: 'tenant' | 'customer' | 'line' | 'request';
}

export interface PatchCustomerQuoteResponse {
  clientQuote: ClientQuote;
  lineStageAdvanced: boolean;
}

export interface ListCustomerQuotesResponse {
  clientQuotes: ClientQuote[];
}

/** GET /api/customer-quotes/:id — detail, includes html_snapshot. */
export interface GetCustomerQuoteResponse {
  clientQuote: ClientQuote; // html present
}

/** POST /api/customer-quotes/:id/regenerate — re-render a draft in place. */
export interface RegenerateCustomerQuoteResponse {
  clientQuote: ClientQuote; // html present
  breakdown: GenerateCustomerQuoteResponse['breakdown'];
  markupSource: 'tenant' | 'customer' | 'line' | 'request';
}

/** POST /api/customer-quotes/:id/send-email — send the quote AS the email body.
 *  The PDF is unaffected; this delivers the quote in the message itself so it
 *  lands readable in the primary inbox, and marks the quote sent. */
export interface SendCustomerQuoteEmailResponse {
  status: 'sent';
  quoteRef: string;
  subject: string;
  to: string;
  acceptLinkIncluded: boolean;
  /** True when the vendor gave us no cert/paperwork, so the email says we are
   *  confirming it rather than claiming it. Surfaced so the broker knows to
   *  chase the vendor. */
  documentationPending: boolean;
  sentAt: string | null;
}

/** DELETE /api/customer-quotes/:id — discard a draft. */
export interface DeleteCustomerQuoteResponse {
  deleted: string;
}

// =====================================================================
// Vendor purchase orders — APD's PO to a vendor (broker-internal doc)
// =====================================================================

export type VendorPoStatus = 'draft' | 'sent' | 'cancelled';
export type VendorPoShipToMode = 'customer_direct' | 'apd_warehouse' | 'logistics_partner';

export interface VendorPurchaseOrder {
  id: string;
  ref: string;
  status: VendorPoStatus;
  rfqRef: string;
  rfqLineItemId: string;
  lineNumber: number;
  vendorName: string | null;
  qty: number;
  currency: Currency;
  vendorUnitPriceCents: number;
  vendorTotalCents: number;
  shipToMode: VendorPoShipToMode;
  shipToText: string;
  paymentTerms: string | null;
  certsRequired: string | null;
  requestedDeliveryDate: string | null;
  /** 1 is the original. Anything higher means this PO has been amended and a
   *  vendor may hold an earlier copy bearing the same number. */
  revision?: number;
  generatedAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  /** Present on detail / commit / regenerate responses; omitted from list. */
  html?: string;
}

export interface GenerateVendorPoRequest {
  rfqLineItemPublicId: string;
  vendorQuotePublicId: string;
  clientQuotePublicId?: string;
  qty?: number;
  shipToMode: VendorPoShipToMode;
  shipToText: string;
  paymentTerms?: string;
  certsRequired?: string;
  requestedDeliveryDate?: string;
  notes?: string;
}

export interface GenerateVendorPoResponse {
  rfqRef: string;
  quoteRef: string | null;
  qty: number;
  currency: Currency;
  vendorUnitCents: number;
  vendorTotalCents: number;
  html: string;
}

export interface CommitVendorPoResponse {
  vendorPurchaseOrder: VendorPurchaseOrder; // includes html
}

export interface RegenerateVendorPoResponse {
  vendorPurchaseOrder: VendorPurchaseOrder;
}

export interface ReviseVendorPoResponse {
  vendorPurchaseOrder: VendorPurchaseOrder;
  revision: number;
  supersededRevision: number;
}

/** One superseded document, exactly as the vendor received it. Append-only on
 *  the server: these rows are never updated or deleted. */
export interface VendorPoRevision {
  publicId: string;
  revision: number;
  statusAtSupersede: string;
  sentAt: string | null;
  changeReason: string;
  supersededAt: string;
  shipToText: string | null;
  paymentTerms: string | null;
  qty: number | null;
  vendorTotalCents: number | null;
}

export interface VendorPoRevisionsResponse {
  currentRevision: number;
  revisions: VendorPoRevision[];
}

export interface PatchVendorPoResponse {
  vendorPurchaseOrder: VendorPurchaseOrder;
  lineStageAdvanced: boolean;
}

export interface DeleteVendorPoResponse {
  deleted: string;
}

export interface ListVendorPosResponse {
  vendorPurchaseOrders: VendorPurchaseOrder[];
}

export interface GetVendorPoResponse {
  vendorPurchaseOrder: VendorPurchaseOrder; // includes html
}

// =====================================================================
// Orders — active fulfillment view (lines from customer commitment → shipped)
// =====================================================================

export interface OrderItem {
  lineId: string;
  rfqPublicId: string;
  rfqRef: string;
  customerId: string | null;
  customerName: string;
  partNumber: string;
  altPartNumber: string | null;
  qty: number;
  uom: string;
  stage: LineStage;
  stageChangedAt: string;
  isAog: boolean;
  isUrgent: boolean;
  isFlagged: boolean;
  valueCents: number | null;
  currency: string | null;
  poNumber: string | null;
  proformaRef?: string | null;
  proformaStatus?: string | null;
  /** Part + any upfront core on the live proforma. */
  amountDueCents?: number | null;
  amountDueCurrency?: string | null;
  /** Goods have shipped (or arrived) and the live proforma is not paid. */
  awaitingPayment?: boolean;
}

export interface OrdersResponse {
  orders: OrderItem[];
}

/** POST /api/customer-quotes/combined — render one multi-P/N document from a set of line quotes. */
export interface CombinedQuoteResponse {
  combinedRef: string;
  rfqRef: string;
  generatedAt: string;
  validUntil: string;
  currency: Currency;
  grandTotalCents: number;
  lineCount: number;
  includedQuoteIds: string[];
  html: string;
}

// =====================================================================
// Proforma invoices
// =====================================================================

export type ProformaInvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled' | 'expired';

export interface ProformaInvoice {
  id: string;
  ref: string;
  status: ProformaInvoiceStatus;
  quoteRef: string;
  /** Public id of the source client quote. Match on this, not quoteRef —
   *  re-quoting a line reuses the ref, so refs are not unique per quote. */
  clientQuoteId: string;
  rfqRef: string;
  rfqLineItemId: string;
  lineNumber: number;
  customerPoNumber: string | null;
  qty: number;
  currency: Currency;
  unitPriceCents: number;
  /** Part total. Revenue and margin read this; it never includes a core. */
  totalCents: number;
  /** Upfront exchange core billed on this proforma, or null. */
  coreChargeCents?: number | null;
  /** What the customer owes: totalCents + coreChargeCents. */
  amountDueCents?: number;
  amountPaidCents: number;
  paymentTerms: string | null;
  paymentAccountId: string;
  paymentMethod: string | null;
  paymentReference: string | null;
  paidAt: string | null;
  /** Live Stripe Checkout link (broker can resend); null until generated. */
  stripePaymentLinkUrl: string | null;
  /** ISO; when the stored Checkout link expires (~24h). Null when no link. */
  stripeCheckoutExpiresAt: string | null;
  cardFeeCents: number | null;
  validUntil: string;
  sentAt: string | null;
  cancelledAt: string | null;
  generatedAt: string;
  notes: string | null;
  /** Present on detail responses; omitted from list responses to keep them lightweight. */
  html?: string;
}

export interface GenerateProformaResponse {
  proformaRef: string;
  quoteRef: string;
  rfqRef: string;
  generatedAt: string;
  validUntil: string;
  currency: Currency;
  qty: number;
  unitPriceCents: number;
  totalCents: number;
  paymentTerms: string;
  paymentAccountId: string;
  html: string;
}

export interface GetProformaResponse {
  proformaInvoice: ProformaInvoice;
}

export interface CommitProformaResponse {
  proformaInvoice: ProformaInvoice;
  lineStageAdvanced: boolean;
  newLineStage: LineStage;
}

export interface PatchProformaResponse {
  proformaInvoice: ProformaInvoice;
  lineStageAdvanced: boolean;
  newLineStage: LineStage;
}

export interface ListProformaInvoicesResponse {
  proformaInvoices: ProformaInvoice[];
}

export interface CardPaymentLinkResponse {
  url: string;
  chargeCents: number;
  feeCents: number;
  invoiceCents: number;
  currency: Currency;
  expiresAt: string | null;
}

export interface CardPaymentSettings {
  enabled: boolean;
  feePctBps: number;
  feeFixedCents: number;
}

export interface CardPaymentSettingsResponse {
  settings: CardPaymentSettings;
  /** False when the server has no Stripe keys — the feature is inert. */
  stripeConfigured: boolean;
}

// =====================================================================
// Shipments
// =====================================================================

export type ShipmentStatus = 'preparing' | 'shipped' | 'in_transit' | 'delivered' | 'exception';

export interface Shipment {
  id: string;
  ref: string;
  status: ShipmentStatus;
  proformaRef: string;
  /** Public id of the source proforma. Match on this, not proformaRef —
   *  a re-issued proforma reuses the ref. */
  proformaInvoiceId: string;
  rfqRef: string;
  rfqLineItemId: string;
  lineNumber: number;
  vendorPoRef: string | null;
  carrier: string | null;
  awbOrTracking: string | null;
  aesItn: string | null;
  shipDate: string | null;
  estimatedDeliveryAt: string | null;
  deliveredAt: string | null;
  /** decimals come over the wire as strings */
  grossWeightKg: string | null;
  netWeightKg: string | null;
  packageCount: number | null;
  packageDimensions: string | null;
  shipToAddress: string | null;
  shipToCarrierAccount: string | null;
  notes: string | null;
  generatedAt: string;
  updatedAt: string;
}

export interface CreateShipmentResponse {
  shipment: Shipment;
  lineStageAdvanced: boolean;
  newLineStage: LineStage;
}

export interface PatchShipmentResponse {
  shipment: Shipment;
  lineStageAdvanced: boolean;
  newLineStage: LineStage;
}

export interface ListShipmentsResponse {
  shipments: Shipment[];
}

// =====================================================================
// Commercial invoices
// =====================================================================

export type CommercialInvoiceStatus = 'draft' | 'issued' | 'superseded';

export interface CommercialInvoice {
  id: string;
  ref: string;
  status: CommercialInvoiceStatus;
  proformaRef: string;
  quoteRef: string;
  rfqRef: string;
  /** Null when issued before the shipment was booked. */
  shipmentRef: string | null;
  rfqLineItemId: string;
  lineNumber: number;
  qty: number;
  currency: Currency;
  unitPriceCents: number;
  totalCents: number;
  customerPoNumber: string | null;
  shipDate: string | null;
  aesItn: string | null;
  awbOrTracking: string | null;
  carrier: string | null;
  htsCode: string | null;
  eccn: string | null;
  countryOfOrigin: string | null;
  grossWeightKg: string | null;
  netWeightKg: string | null;
  packageCount: number | null;
  portOfExport: string | null;
  portOfEntry: string | null;
  endUse: string | null;
  endUser: string | null;
  licenseCitation: string | null;
  shipToAddress: string | null;
  soldToAddress?: string | null;
  shipFromLocationId?: string | null;
  shipFromLabel?: string | null;
  /** The proforma's status at issue; null on CIs from before 2026-10-23 (all paid). */
  proformaStatusAtIssue?: string | null;
  issuedAt: string | null;
  supersededAt: string | null;
  generatedAt: string;
  notes: string | null;
  /** Present on detail responses; omitted from list responses to keep them lightweight. */
  html?: string;
}

export interface GenerateCommercialInvoiceResponse {
  ciRef: string;
  proformaRef: string;
  quoteRef: string;
  rfqRef: string;
  /** Null when issued before the shipment was booked. */
  shipmentRef: string | null;
  generatedAt: string;
  currency: Currency;
  qty: number;
  unitPriceCents: number;
  totalCents: number;
  html: string;
  /** Whether the proforma is paid: the CI says "Paid in full" or states terms. */
  paid?: boolean;
  /** The ECCN the document will state (typed, else the vendor quote's). */
  eccn?: string | null;
}

export interface CommitCommercialInvoiceResponse {
  commercialInvoice: CommercialInvoice;
}

export interface GetCommercialInvoiceResponse {
  /** Detail response — includes the frozen html snapshot. */
  commercialInvoice: CommercialInvoice;
}

export interface PatchCommercialInvoiceResponse {
  commercialInvoice: CommercialInvoice;
}

export interface ListCommercialInvoicesResponse {
  commercialInvoices: CommercialInvoice[];
}

// =====================================================================
// Vendors (Slice 11A)
// =====================================================================

export interface Vendor {
  id: string;
  legalName: string;
  displayName: string | null;
  partsbaseProfileUrl: string | null;
  reliabilityRating: number | null;
  aogCapable: boolean;
  brokerFriendly: boolean;
  paymentTerms: string | null;
  preferredCurrency: string | null;
  lastOrderDate: string | null;
  lastQuotedAt: string | null;
  lastWonAt: string | null;
  totalQuotesReceived: number;
  totalWins: number;
  /** decimal-as-string from drizzle: "12.50" = 12.50%. */
  winRate: string;
  avgResponseHours: string | null;
  avgLeadTimeDays: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // Primary contact (populated only by the list endpoint — most useful contact
  // at this vendor: full_name first, then phone, then most recent).
  contactFullName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

export interface VendorContact {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  notes: string | null;
  createdAt: string;
}

export type VendorSpecializationKind = 'aircraft' | 'part_category' | 'region' | 'engine';

export interface VendorSpecialization {
  /** Internal UUID — used as the DELETE key. */
  id: string;
  kind: VendorSpecializationKind;
  value: string;
  source: 'manual' | 'derived_from_quotes';
  createdAt: string;
}

export interface VendorRecentActivity {
  vendorQuotePublicId: string;
  rfqRef: string;
  partNumber: string;
  lineNumber: number;
  unitPriceCents: number;
  currency: Currency;
  qtyOffered: number;
  createdAt: string;
}

export interface VendorTopPartNumber {
  partNumber: string;
  quotes: number;
}

export interface ListVendorsResponse {
  vendors: Vendor[];
}

export interface VendorScorecardResponse {
  vendor: Vendor;
  contacts: VendorContact[];
  specializations: VendorSpecialization[];
  recentActivity: VendorRecentActivity[];
  topPartNumbers: VendorTopPartNumber[];
}

export interface CreateVendorResponse {
  vendor: Vendor;
}

export interface PatchVendorResponse {
  vendor: Vendor;
}

export interface CreateVendorContactResponse {
  vendorContact: VendorContact;
}

export interface PatchVendorContactResponse {
  vendorContact: VendorContact;
}

export interface CreateVendorSpecializationResponse {
  vendorSpecialization: VendorSpecialization;
}

export interface VendorSpecializationValuesResponse {
  kind: VendorSpecializationKind;
  /** Distinct existing tag values for this kind (plus RFQ platforms for aircraft). */
  values: string[];
}

// =====================================================================
// Customers (Slice 14)
// =====================================================================

export interface CustomerListItem {
  id: string;
  legalName: string;
  displayName: string | null;
  emailDomain: string | null;
  countryCode: string | null;
  isFaaPart145: boolean;
  isMilitary: boolean;
  createdAt: string;
  lifetimeRfqs: number;
  rfqs90d: number;
  lastRfqAt: string | null;
  lifetimePaidCents: number;
  lifetimePaidCount: number;
  hasAog: boolean;
  aogCount: number;
  // Primary contact (most recent contact at this customer with full_name/phone preferred)
  contactFullName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
}

export interface ListCustomersResponse {
  customers: CustomerListItem[];
}

export interface Customer {
  id: string;
  legalName: string;
  displayName: string | null;
  emailDomain: string | null;
  countryCode: string | null;
  isFaaPart145: boolean;
  faaCertNumber: string | null;
  isMilitary: boolean;
  carrierCertNumber: string | null;
  /** decimal-as-string from drizzle, e.g. "12.50" = 12.5%. Null = use tenant default. */
  markupPctOverride: string | null;
  bisLastScreenedAt: string | null;
  bisScreenStatus: string | null;
  bisScreenPdfKey: string | null;
  bisScreenPdfUrl: string | null;
  bisScreenDocumentFilename: string | null;
  ghlContactId: string | null;
  /** Free-text broker/lead context. Chatbot leads store the conversation here. */
  notes: string | null;
  /** First-touch channel: 'chatbot' | 'web_form' | 'email' | 'manual' | null. */
  source: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerContact {
  id: string;
  fullName: string | null;
  email: string;
  phone: string | null;
  jobTitle: string | null;
  createdAt: string;
}

export interface CustomerKpis {
  lifetimeRfqs: number;
  rfqs90d: number;
  firstRfqAt: string | null;
  lastRfqAt: string | null;
  lifetimePaidCents: number;
  lifetimePaidCount: number;
  avgOrderCents: number | null;
  aogCount: number;
  lastAogAt: string | null;
  /** null when no quotes were sent yet; 0–100 otherwise. */
  winRate: number | null;
  winRateAccepted: number;
  winRateResolved: number;
  avgResponseHours: number | null;
}

export interface CustomerScorecardResponse {
  customer: Customer;
  kpis: CustomerKpis;
  topPartNumbers: Array<{ partNumber: string; rfqCount: number }>;
  topAircraftPlatforms: Array<{ platform: string; rfqCount: number }>;
  contacts: CustomerContact[];
  recentActivity: Array<{
    rfqPublicId: string;
    rfqRef: string;
    isAog: boolean;
    isUrgent: boolean;
    receivedAt: string;
    sourceChannel: string | null;
    lineCount: number;
    /** Map of stage → count of line items in that stage. Empty {} when RFQ has no lines. */
    stageCounts: Partial<Record<LineStage, number>>;
    /** Outbound channels this RFQ has been posted to. */
    sourcingChannels: SourcingChannel[];
  }>;
}

// =====================================================================
// Manual customer + RFQ creation (Add Customer / New RFQ modals)
// =====================================================================

export interface CreateCustomerRequest {
  legalName: string;
  displayName?: string;
  emailDomain?: string;
  countryCode?: string;
  isFaaPart145?: boolean;
  faaCertNumber?: string;
  isMilitary?: boolean;
  carrierCertNumber?: string;
  markupPctOverride?: number;
  contact?: {
    email: string;
    fullName?: string;
    phone?: string;
    jobTitle?: string;
  };
}

export interface CreateCustomerResponse {
  customer: Customer;
  contacts: CustomerContact[];
}

export type ManualRfqCustomer =
  | { kind: 'existing'; publicId: string }
  | {
      kind: 'new';
      legalName: string;
      displayName?: string;
      emailDomain?: string;
      countryCode?: string;
    };

export type ManualRfqContact =
  | { kind: 'existing'; publicId: string }
  | {
      kind: 'new';
      email: string;
      fullName?: string;
      phone?: string;
      jobTitle?: string;
    }
  | { kind: 'none' };

export interface ManualRfqLineItem {
  partNumber: string;
  altPartNumber?: string;
  description?: string;
  qty: number;
  uom?: string;
  conditionRequested?: ConditionCode;
  certRequested?: CertType;
  isAog?: boolean;
  notes?: string;
}

export interface CreateRfqRequest {
  customer: ManualRfqCustomer;
  contact: ManualRfqContact;
  rfq: {
    isAog: boolean;
    isUrgent?: boolean;
    notes?: string;
    receivedAt?: string;
  };
  lineItems: ManualRfqLineItem[];
}

export interface CreateRfqResponse {
  rfq: {
    id: string;
    ref: string;
    isAog: boolean;
    customerId: string;
    contactId: string | null;
    defaultCurrency: string | null;
    defaultIncoterms: string | null;
    sourceChannel: string | null;
    notes: string | null;
    receivedAt: string;
  };
  customer: {
    id: string;
    legalName: string;
    displayName: string | null;
    emailDomain: string | null;
    countryCode: string | null;
  };
  contact: {
    id: string;
    email: string;
    fullName: string | null;
    phone: string | null;
    jobTitle: string | null;
  } | null;
  lineItems: Array<{
    id: string;
    lineNumber: number;
    partNumber: string;
    altPartNumber: string | null;
    description: string | null;
    qty: number;
    uom: string;
    conditionRequested: string | null;
    certRequested: string | null;
    isAog: boolean;
    stage: LineStage;
  }>;
}

// =====================================================================
// RFQ + line item notes (Slice 14b)
// =====================================================================

export interface PatchRfqNotesResponse {
  rfq: {
    id: string;
    ref: string;
    notes: string | null;
    updatedAt: string;
  };
}

export interface PatchLineItemNotesResponse {
  lineItem: {
    id: string;
    lineNumber: number;
    partNumber: string;
    notes: string | null;
    updatedAt: string;
  };
}

export interface PatchCustomerResponse {
  customer: Customer;
}

export interface PatchContactResponse {
  contact: CustomerContact;
}

export interface CreateContactResponse {
  contact: CustomerContact;
}

export interface ConversationMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  channel: string; // 'email' | 'sms' | 'whatsapp' | 'call' | 'social' | 'other'
  subject: string | null;
  body: string | null;
  dateAdded: string | null;
  status: string | null;
}

export interface CustomerConversationResponse {
  conversationId: string | null;
  ghlDeepLink: string | null;
  otherConversationCount: number;
  contactId: string | null;       // GHL contact id to send a reply to
  contactName: string | null;
  messages: ConversationMessage[];
  note?: string;
}

export interface SendConversationMessageResponse {
  ok: boolean;
  channel: 'SMS' | 'Email';
  messageId: string | null;
  conversationId: string | null;
}

// =====================================================================
// Dashboard (Slice 11B)
// =====================================================================

export interface DashboardOverviewResponse {
  kpis: {
    /** Shipped or delivered while the proforma is still unpaid. */
    shippedAwaitingPayment?: { count: number; byCurrency: Array<{ currency: string; amountDueCents: number }> };
    openRfqs: number;
    paid30dCount: number;
    paid30dTotalCents: number;
    shipmentsActive: number;
    vendorsTotal: number;
    accepted30d: number;
    /** null when no quotes were sent in the window. */
    winRate30d: number | null;
  };
  recentRfqs: Array<{
    publicId: string;
    ref: string;
    isAog: boolean;
    isUrgent: boolean;
    receivedAt: string;
    sourceChannel: string | null;
  }>;
}

// =====================================================================
// Vendor matrix / by-PN / coverage gaps (Slice 11B)
// =====================================================================

export type MatrixDimension = 'aircraft' | 'part_category';

export interface MatrixCellData {
  quoteCount: number;
  winCount: number;
  /** null when quoteCount=0; 0–100 otherwise. */
  winRate: number | null;
  avgLeadTimeDays: number | null;
}

export interface VendorMatrixResponse {
  dimension: MatrixDimension;
  rows: Array<{ id: string; name: string }>;
  cols: string[];
  /** Sparse cell map indexed by `${vendorId}::${colKey}`. */
  cells: Record<string, MatrixCellData>;
}

export interface VendorByPnRecommendation {
  id: string;
  name: string;
  winRate: string;
  quoteCount: number;
}

export interface VendorByPnExactMatch extends VendorByPnRecommendation {
  lastQuoteAt: string;
  lastUnitPriceCents: number;
  lastCurrency: Currency;
  lastLeadTimeDays: number | null;
}

export interface VendorByPnSimilar extends VendorByPnRecommendation {
  samplePartNumber: string;
}

export interface VendorByPnResponse {
  partNumber: string;
  platform: string | null;
  exactMatch: VendorByPnExactMatch[];
  similar: VendorByPnSimilar[];
  bySpec: VendorByPnRecommendation[];
}

export interface CoverageGapsResponse {
  platformsWithThinCoverage: Array<{
    platform: string;
    rfqCount: number;
    vendorCount: number;
  }>;
  untaggedPlatforms: Array<{
    platform: string;
    rfqCount: number;
  }>;
}

// =====================================================================
// Vendor quote intake — the quotes@ email inbox (Slice 13)
// =====================================================================

export type VendorQuoteIntakeStatus =
  | 'unmatched'
  | 'ambiguous'
  | 'auto_committed'
  | 'manually_committed'
  | 'discarded'
  | 'parse_failed';

export interface VendorQuoteIntakeListItem {
  id: string;
  status: VendorQuoteIntakeStatus;
  subject: string | null;
  senderEmail: string | null;
  senderName: string | null;
  vendorName: string | null;
  quoteCount: number;
  partNumbers: string[];
  parserConfidence: number | null;
  matchedVendorQuoteId: string | null;
  receivedAt: string | null;
  createdAt: string;
  triagedAt: string | null;
  discardReason: string | null;
}

export interface VendorQuoteIntakeListResponse {
  items: VendorQuoteIntakeListItem[];
  nextCursor: string | null;
}

export interface VendorQuoteIntakeCandidateLine {
  id: string;
  partNumber: string;
  altPartNumber: string | null;
  qty: number;
  stage: LineStage;
  rfqRef: string;
  rfqPublicId: string;
  customerName: string | null;
  isOpen: boolean;
  receivedAt: string | null;
}

/** Parsed multi-quote tool output (subset the inbox renders). */
export interface VendorQuoteIntakeParsedQuote {
  part_number: string;
  alt_part_number?: string;
  qty_offered: number;
  unit_price: number;
  currency: string;
  condition_offered?: string;
  cert_offered?: string;
  lead_time_days?: number;
  incoterms?: string;
  fob_location?: string;
  valid_until?: string;
  eccn?: string;
  hts_code?: string;
  country_of_origin?: string;
  hazmat_class?: string;
  notes?: string;
}

export interface VendorQuoteIntakeParsed {
  vendor: { name: string; contact_name?: string; contact_email?: string };
  quotes: VendorQuoteIntakeParsedQuote[];
  overall_confidence: number;
  warnings: string[];
}

export interface VendorQuoteIntakeDetailResponse {
  intake: VendorQuoteIntakeListItem & {
    rawText: string;
    parsedJson: VendorQuoteIntakeParsed | null;
    parserModel: string | null;
    candidateLineItemIds: string[] | Record<string, string[]> | null;
  };
  /** Keyed by line publicId → resolved triage context. */
  candidateLines: Record<string, VendorQuoteIntakeCandidateLine>;
}

export interface VendorQuoteIntakeTriageResponse {
  intake: VendorQuoteIntakeListItem;
  vendorQuote: { id: string; reviewStatus: string } | null;
}

// =====================================================================
// RFQ parser (existing)
// =====================================================================

export interface ParsedRfqResponse {
  rfq: {
    id: string;
    ref: string;
    isAog: boolean;
    customerId: string;
    contactId: string | null;
    defaultCurrency: string | null;
    defaultIncoterms: string | null;
    receivedAt: string;
  };
  lineItems: Array<{
    id: string;
    lineNumber: number;
    partNumber: string;
    altPartNumber: string | null;
    description: string | null;
    qty: number;
    uom: string;
    conditionRequested: string | null;
    certRequested: string | null;
    isAog: boolean;
    stage: LineStage;
  }>;
  parser: {
    model: string;
    confidence: number;
    warnings: string[];
    durationMs: number;
  };
}

// --- Alternate Part Finder (DLA FLIS / PUB LOG NSN cross-reference) ---
export interface AltPart {
  nsn: string;            // formatted FSC-NN-NNN-NNNN
  cageCode: string;
  refNumber: string;      // alternate part number
  companyName: string | null;
  itemName: string | null;
}
export interface AltPartsResponse {
  originalPartNumber: string;
  normalized: string;
  nsns: Array<{ nsn: string; fsc: string; itemName: string | null }>;
  altParts: AltPart[];
  truncated: boolean;
  lookupStatus: 'found' | 'not_found';
  source: 'flis_publog';
  /** What the match actually means. A shared NSN is federal-catalog cross
   *  reference evidence, NOT an airworthiness or interchangeability approval —
   *  the UI must label it as such. */
  relationshipType: 'DLA_SAME_NSN_REFERENCE';
  /** Vintage of the loaded PUB LOG snapshot; null if unknown. */
  dataset?: { pubDate: string | null; loadedAt: string | null } | null;
}

// ---------------------------------------------------------------------------
// Part Intelligence (Phase 1B). Evidence + machine assessment + human
// decisions per RFQ line. The GET reads stored evidence only: it never calls
// PartsBase. See docs/part-intelligence/FEATURE_CHARTER.md.
// ---------------------------------------------------------------------------
export type {
  CurrentDecisions as PartIdentityCurrentDecisions,
  DecisionRecord as PartIdentityDecisionRecord,
  DecisionInput as PartIdentityDecisionInput,
} from '@shared/part-intelligence/decisions';
export type { EvidenceSnapshot as PartEvidenceSnapshot } from '@shared/part-intelligence/evidence';

export interface PartIntelligenceLine {
  lineItemId: string;
  /** Exactly as the customer entered it. */
  partNumber: string;
  snapshot: import('@shared/part-intelligence/evidence').EvidenceSnapshot;
  decisions: import('@shared/part-intelligence/decisions').CurrentDecisions;
  history: import('@shared/part-intelligence/decisions').DecisionRecord[];
  nextAction: string;
}
export interface PartIntelligenceResponse {
  rfqId: string;
  catalogSource: string | null;
  lines: PartIntelligenceLine[];
}

export async function getPartIntelligence(rfqPublicId: string, tokenGetter: TokenGetter) {
  return api<PartIntelligenceResponse>(`/api/rfqs/${rfqPublicId}/part-intelligence`, { tokenGetter });
}

export async function recordPartIdentityDecision(
  rfqPublicId: string,
  lineItemPublicId: string,
  input: import('@shared/part-intelligence/decisions').DecisionInput,
  tokenGetter: TokenGetter,
) {
  return api<{ decision: import('@shared/part-intelligence/decisions').DecisionRecord }>(
    `/api/rfqs/${rfqPublicId}/line-items/${lineItemPublicId}/part-intelligence/decisions`,
    { method: 'POST', tokenGetter, idempotencyKey: crypto.randomUUID(), body: JSON.stringify(input) },
  );
}

// ---------------------------------------------------------------------------
// Catalog feedback (Phase 1C). Human review of suspected catalog problems.
// Never writes the catalog; the export is instructions for a later process.
// ---------------------------------------------------------------------------
export type {
  ReviewInput as CatalogReviewInput,
  ReviewRecord as CatalogReviewRecord,
  ReviewStatus as CatalogReviewStatus,
  CorrectionExport as CatalogCorrectionExport,
} from '@shared/part-intelligence/catalog-review';
export type {
  QueueItem as CatalogQueueItem,
  FamilySummary as CatalogFamilySummary,
} from '@shared/part-intelligence/catalog-review';

export interface CatalogQueueResponse {
  items: import('@shared/part-intelligence/catalog-review').QueueItem[];
  catalogAvailable: boolean;
  canReview: boolean;
}

export interface CatalogFeedbackCapability { enabled: boolean; canView: boolean; canFlag: boolean; canReview: boolean }

/** What catalog-feedback UI this user may see. Presentation only: every
 *  catalog route enforces the capability and the role on the server. */
export async function getCatalogFeedbackCapability(tokenGetter: TokenGetter) {
  return api<CatalogFeedbackCapability>('/api/catalog-review/capability', { tokenGetter });
}

export async function getCatalogReviewQueue(tokenGetter: TokenGetter) {
  return api<CatalogQueueResponse>('/api/catalog-review', { tokenGetter });
}

export async function getCatalogFamilies(tokenGetter: TokenGetter) {
  return api<{ families: import('@shared/part-intelligence/catalog-review').FamilySummary[] }>(
    '/api/catalog-review/families', { tokenGetter },
  );
}

export async function recordCatalogReview(
  input: import('@shared/part-intelligence/catalog-review').ReviewInput,
  tokenGetter: TokenGetter,
) {
  return api<{ review: import('@shared/part-intelligence/catalog-review').ReviewRecord }>(
    '/api/catalog-review/reviews',
    { method: 'POST', tokenGetter, idempotencyKey: crypto.randomUUID(), body: JSON.stringify(input) },
  );
}

export async function exportCatalogCorrections(tokenGetter: TokenGetter) {
  return api<import('@shared/part-intelligence/catalog-review').CorrectionExport>('/api/catalog-review/export', { tokenGetter });
}

/** Fire the alt-part intent beacon. Never rejects: the copy already succeeded,
 *  and a failed measurement must not surface as an error to the broker. */
export async function reportAltPartCopy(
  partNumber: string,
  surface: 'rfq_line' | 'search_page',
  tokenGetter: TokenGetter,
): Promise<void> {
  try {
    await api<void>('/api/alt-parts/events', {
      method: 'POST',
      tokenGetter,
      body: JSON.stringify({ event: 'copy_all', surface, partNumber }),
    });
  } catch {
    /* measurement only */
  }
}

// --- PartsBase Real Time Search (read-only seller listings) ---
export interface PartsBaseSellerDto {
  name: string | null;
  sellerId: number | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  cage: string | null;
  certificates: string[];
}

export interface PartsBaseListingDto {
  /** What a future SendRFQ would address. Not used for sending yet. */
  inventoryId: number;
  partNumber: string;
  alternatePartNumber: string | null;
  description: string | null;
  manufacturer: string | null;
  conditionCode: string | null;
  conditionRaw: string | null;
  quantity: number | null;
  /** Integer cents. null when PartsBase reported 0 — their "call for price". */
  unitPriceCents: number | null;
  /** ISO 4217, only when their free-text field actually is a code. */
  currency: string | null;
  /** Their raw word, e.g. "dollars" — ambiguous (CAD/AUD/SGD sellers send it
   *  too), so it must never be rendered as a $ symbol. */
  currencyRaw: string | null;
  uom: string | null;
  lastUpdated: string | null;
  seller: PartsBaseSellerDto;
}

export type PartsBaseListingsResponse =
  | {
      status: 'ok';
      /** Exact part-number matches only — the server filters before sending. */
      listings: PartsBaseListingDto[];
      sellerCount: number;
      truncated: boolean;
      /** False means this answer cost a metered PartsBase call. */
      cached?: boolean;
      cacheAgeMinutes?: number;
      /** Whether THIS user can send an RFQ from these results. Decided by the
       *  server from role plus every send-gate lock; the client never infers it. */
      canSend?: boolean;
      sendBlockedReason?: string;
      maxRecipients?: number;
      /** The quantity a send would ask for, straight from the line item. Server
       *  -computed so what the operator is shown cannot drift from what is sent. */
      sendQuantity?: number;
      /** Inventory ids already sent an RFQ for this part, newest first. */
      alreadySent?: Array<{ inventoryId: number; sentAt: string; rfqRef: string }>;
    }
  | {
      status: 'disabled' | 'not_configured' | 'unavailable' | 'misconfigured';
      message: string;
    };

// --- Billing: receiving (banking) accounts for proforma invoices ---
export interface PaymentAccountDto {
  id: string;
  label: string;
  hint: string;
  isDefault: boolean;
  beneficiaryName: string;
  beneficiaryAddress: string;
  bankName: string;
  bankAddress: string;
  swiftBic: string;
  abaRouting: string;
  accountNumber: string;
  intermediaryBic: string;
  feeTerms: string;
}
export interface ListPaymentAccountsResponse {
  accounts: PaymentAccountDto[];
}


// --- PartsBase: operator-initiated RFQ send ---
export interface PartsBaseSendResponse {
  /** `delivery_unknown` means the outcome is genuinely ambiguous — the RFQ may
   *  have been delivered. It is never safe to resend on this. */
  status: 'sent' | 'delivery_unknown';
  partsbaseRfqId: number | null;
  recipients: number;
  notQualifiedCount: number;
  trackingRef: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Compliance — canonical organization profile
//
// Nothing in these types carries a tax identifier or a date of birth. The server
// does not select those columns, and these shapes exist partly to make that
// visible at the call site: `taxIdLast4` is the whole display surface, and a
// reveal is a separate, deliberate request.
// ---------------------------------------------------------------------------

export type PartnerContactRole =
  | 'compliance' | 'purchasing' | 'accounting' | 'shipping' | 'signer' | 'executive' | 'general';

export interface PartnerComplianceContact {
  publicId: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  roles: PartnerContactRole[];
  isActive: boolean;
}

export interface PartnerComplianceRegistration {
  publicId: string;
  kind: 'company' | 'tax' | 'government' | 'other';
  jurisdictionCountry: string;
  jurisdictionSubdivision: string | null;
  legalEntityType: string | null;
  incorporationDate: string | null;
  registrationNumber: string | null;
  filingAuthority: string | null;
  /** The ONLY part of a tax identifier that crosses the wire by default. */
  taxIdLast4: string | null;
  taxIdKind: string | null;
  isPrimary: boolean;
}

export interface PartnerComplianceOwner {
  publicId: string;
  holderKind: 'person' | 'organization';
  relationship: 'beneficial_owner' | 'shareholder' | 'officer' | 'director' | 'signatory';
  firstName: string | null;
  surname: string | null;
  citizenshipCountry: string | null;
  organizationName: string | null;
  holderPartnerId: string | null;
  ownershipPct: string | null;
  title: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface PartnerComplianceAddress {
  publicId: string;
  kind: 'billing' | 'shipping' | 'remit' | 'registered' | 'headquarters';
  line1: string;
  line2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string | null;
  isDefault: boolean;
}

export interface PartnerComplianceProfile {
  partner: { publicId: string; legalName: string };
  contacts: PartnerComplianceContact[];
  registrations: PartnerComplianceRegistration[];
  owners: PartnerComplianceOwner[];
  addresses: PartnerComplianceAddress[];
  completeness: {
    hasRegisteredAddress: boolean;
    hasComplianceContact: boolean;
    hasPrimaryRegistration: boolean;
    ownerCount: number;
    equityRecordedPct: number;
  };
}

export function getPartnerComplianceProfile(partnerPublicId: string, tokenGetter: TokenGetter) {
  return api<PartnerComplianceProfile>(
    `/api/trading-partners/${partnerPublicId}/compliance`,
    { tokenGetter },
  );
}

// ---------------------------------------------------------------------------
// Compliance — templates and submissions
// ---------------------------------------------------------------------------

export interface ComplianceTemplateOption {
  versionId: string;
  templateId: string;
  code: string;
  name: string;
  kind: string;
  ownerKind: 'platform' | 'tenant';
  versionLabel: string;
  scope: 'organization' | 'transaction' | 'line_item' | 'serial_unit';
}

export type ComplianceProvenance =
  | 'prefilled_unconfirmed' | 'prefilled_confirmed'
  | 'entered_by_customer' | 'entered_by_apd' | 'imported';

export type ComplianceSubmissionStatus =
  | 'draft' | 'submitted' | 'under_review' | 'changes_requested'
  | 'accepted' | 'superseded' | 'void';

export interface ComplianceSubmissionListItem {
  publicId: string;
  status: ComplianceSubmissionStatus;
  scope: string;
  revision: number;
  createdAt: string;
  submittedAt: string | null;
  templateCode: string;
  templateName: string;
  versionLabel: string;
  partnerPublicId: string;
  partnerLegalName: string;
}

export interface ComplianceField {
  key: string;
  label: string;
  type: 'text' | 'long_text' | 'integer' | 'date' | 'country' | 'select' | 'boolean';
  options?: string[];
  required: boolean;
  canonicalSource?: string;
  consequential?: boolean;
  visibleWhen?: { field: string; equals: string[] };
  helpText?: string;
  maxLength?: number;
}

export interface ComplianceFieldSchema {
  key: string;
  title: string;
  sections: { key: string; title: string; description?: string; fields: ComplianceField[] }[];
}

export interface ComplianceGateBlocker {
  kind: 'required_missing' | 'consequential_unconfirmed' | 'clause_contradicted'
    | 'clause_unanswered' | 'serial_unknown';
  target: string;
  label: string;
  message: string;
}

export interface ComplianceDiffRow {
  fieldKey: string;
  label: string;
  canonicalValue: string | null;
  submittedValue: string | null;
  provenance: ComplianceProvenance;
}

export interface ComplianceSubmissionDetail {
  submission: {
    publicId: string;
    status: ComplianceSubmissionStatus;
    scope: string;
    revision: number;
    notes: string | null;
    reviewNotes: string | null;
    createdAt: string;
    submittedAt: string | null;
    reviewedAt: string | null;
  };
  template: { code: string; name: string; versionLabel: string; fieldSchemaKey: string };
  partner: { publicId: string; legalName: string };
  schema: ComplianceFieldSchema;
  values: { fieldKey: string; value: string | null; provenance: ComplianceProvenance }[];
  clauses: {
    clauseRef: string; exactText: string; answerType: string;
    displayOrder: number; isRequired: boolean;
  }[];
  clauseAnswers: { clauseRef: string; answer: string }[];
  transactionRefCount: number;
  gate: { blockers: ComplianceGateBlocker[]; canSubmit: boolean };
  canonicalDiff: ComplianceDiffRow[];
  units: { publicId: string; partNumber: string | null; serialNumber: string | null }[];
  signatureMethods: string[];
  /** Present only once signed. The evidence a reviewer checks. */
  signature: ComplianceSignature | null;
  request: ComplianceRequestSummary | null;
  document: ComplianceDocumentSummary | null;
}

export interface ComplianceSignature {
  publicId: string;
  method: string;
  signerKind: 'customer' | 'internal';
  signerName: string;
  signerTitle: string | null;
  signerEmail: string | null;
  signedAt: string;
  signedIp: string | null;
  signedUserAgent: string | null;
  /** The EXACT words that were on screen, copied at signing. */
  declarationText: string;
  contentSha256: string | null;
}

export type ComplianceRequestStatus =
  | 'draft' | 'active' | 'completed' | 'revoked' | 'expired' | 'cancelled';

export interface ComplianceRequestSummary {
  publicId: string;
  status: ComplianceRequestStatus;
  recipientEmail: string;
  recipientName: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  /**
   * Something retrieved the link. Very possibly a mail scanner — do not render
   * this as "the customer opened it".
   */
  firstFetchedAt: string | null;
  /** A person deliberately began. */
  humanStartedAt: string | null;
  completedAt: string | null;
}

export interface ComplianceRequestListItem extends ComplianceRequestSummary {
  lastActivityAt: string | null;
  submissionPublicId: string;
  submissionStatus: ComplianceSubmissionStatus;
}

export interface ComplianceDocumentSummary {
  publicId: string;
  filename: string;
  byteSize: number;
  sha256: string;
  generatedAt: string;
  renderer: string;
}

export function listComplianceTemplates(tokenGetter: TokenGetter) {
  return api<{
    /** Whether a customer link may be issued for this tenant. */
    customerWorkflowEnabled: boolean;
    templates: ComplianceTemplateOption[];
  }>('/api/compliance/templates', { tokenGetter });
}

export function listComplianceSubmissions(tokenGetter: TokenGetter) {
  return api<{ submissions: ComplianceSubmissionListItem[] }>(
    '/api/compliance/submissions', { tokenGetter },
  );
}

export function getComplianceSubmission(publicId: string, tokenGetter: TokenGetter) {
  return api<ComplianceSubmissionDetail>(
    `/api/compliance/submissions/${publicId}`, { tokenGetter },
  );
}

export function createComplianceSubmission(
  body: {
    templateVersionPublicId: string;
    subjectPartnerPublicId: string;
    rfqLineItemPublicId?: string | null;
    serializedUnitPublicIds?: string[];
    /** Internal note. Never shown to the customer. The server has always
     *  accepted this; the client type omitted it. */
    notes?: string | null;
  },
  tokenGetter: TokenGetter,
) {
  return api<{ publicId: string; status: string }>('/api/compliance/submissions', {
    method: 'POST', tokenGetter, idempotencyKey: crypto.randomUUID(), body: JSON.stringify(body),
  });
}

export function setComplianceValues(
  publicId: string,
  values: { fieldKey: string; value: string | null; provenance: 'entered_by_apd' | 'prefilled_confirmed' }[],
  tokenGetter: TokenGetter,
) {
  return api<{ updated: number; gate: { blockers: ComplianceGateBlocker[]; canSubmit: boolean } }>(
    `/api/compliance/submissions/${publicId}/values`,
    { method: 'PATCH', tokenGetter, body: JSON.stringify({ values }) },
  );
}

export function setComplianceClauses(
  publicId: string,
  answers: { clauseRef: string; answer: string }[],
  tokenGetter: TokenGetter,
) {
  return api<{ gate: { blockers: ComplianceGateBlocker[]; canSubmit: boolean } }>(
    `/api/compliance/submissions/${publicId}/clauses`,
    { method: 'PATCH', tokenGetter, body: JSON.stringify({ answers }) },
  );
}

export function submitComplianceSubmission(publicId: string, tokenGetter: TokenGetter) {
  return api<{ status: string }>(`/api/compliance/submissions/${publicId}/submit`, {
    method: 'POST', tokenGetter, body: '{}',
  });
}

export function acceptComplianceSubmission(
  publicId: string, reviewNotes: string | null, tokenGetter: TokenGetter,
) {
  return api<{ status: string }>(`/api/compliance/submissions/${publicId}/accept`, {
    method: 'POST', tokenGetter, body: JSON.stringify({ reviewNotes }),
  });
}

export function requestComplianceChanges(
  publicId: string, reviewNotes: string, tokenGetter: TokenGetter,
) {
  return api<{ status: string }>(`/api/compliance/submissions/${publicId}/request-changes`, {
    method: 'POST', tokenGetter, body: JSON.stringify({ reviewNotes }),
  });
}

export function reviseComplianceSubmission(publicId: string, tokenGetter: TokenGetter) {
  return api<{ publicId: string; revision: number }>(
    `/api/compliance/submissions/${publicId}/revise`,
    { method: 'POST', tokenGetter, body: '{}' },
  );
}

export function ensureStandardEusTemplate(tokenGetter: TokenGetter) {
  return api<{ templatePublicId: string; versionPublicId: string; created: boolean }>(
    '/api/compliance/templates/ensure-standard-eus',
    { method: 'POST', tokenGetter, body: '{}' },
  );
}

export interface TradingPartnerListItem {
  id: string;
  legalName: string;
  dba: string | null;
  countryCode: string | null;
  roles: { customer: boolean; vendor: boolean };
  status: string;
}

/**
 * Trading partners, optionally filtered.
 *
 * `q` matters at this tenant's scale: production has 1,272 partners and the
 * endpoint caps `limit` at 200, so an unfiltered list is BOTH truncated and
 * unusable in a picker. The server does the matching (see ListQuery in
 * server/routes/trading-partners.ts) rather than the client filtering a
 * truncated page, which would silently hide anything past the cap.
 */
export function listTradingPartners(
  tokenGetter: TokenGetter,
  opts: { q?: string; status?: string; role?: 'customer' | 'vendor'; limit?: number } = {},
) {
  const params = new URLSearchParams();
  if (opts.q?.trim()) params.set('q', opts.q.trim());
  if (opts.status) params.set('status', opts.status);
  if (opts.role) params.set('role', opts.role);
  params.set('limit', String(opts.limit ?? 200));
  return api<{ tradingPartners: TradingPartnerListItem[] }>(
    `/api/trading-partners?${params.toString()}`, { tokenGetter },
  );
}

// ---------------------------------------------------------------------------
// Customer compliance requests
//
// `accessUrl` comes back exactly ONCE, on the call that mints it. It is a live
// bearer credential: it is never stored in plaintext, never returned again, and
// must not be written to component state that outlives the dialog showing it.
// ---------------------------------------------------------------------------

export interface IssuedComplianceRequest {
  requestPublicId: string;
  expiresAt: string;
  recipientEmail?: string;
  accessUrl: string;
  accessUrlNote: string;
}

export function listComplianceRequests(tokenGetter: TokenGetter) {
  return api<{ requests: ComplianceRequestListItem[] }>(
    '/api/compliance/requests', { tokenGetter },
  );
}

export interface ComplianceCredentialHistoryRow {
  publicId: string;
  issuedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
  useCount: number;
  delivered: boolean;
}

export function getComplianceRequest(requestPublicId: string, tokenGetter: TokenGetter) {
  return api<{
    request: ComplianceRequestSummary & {
      message: string | null; revokedAt: string | null; lastActivityAt: string | null;
    };
    credentials: ComplianceCredentialHistoryRow[];
  }>(`/api/compliance/requests/${requestPublicId}`, { tokenGetter });
}

export function issueComplianceRequest(
  submissionPublicId: string,
  body: {
    recipientEmail: string;
    recipientName?: string | null;
    recipientContactPublicId?: string | null;
    message?: string | null;
    expiresInDays?: number;
  },
  tokenGetter: TokenGetter,
) {
  return api<IssuedComplianceRequest>(
    `/api/compliance/submissions/${submissionPublicId}/request`,
    { method: 'POST', tokenGetter, body: JSON.stringify(body) },
  );
}

export function resendComplianceRequest(requestPublicId: string, tokenGetter: TokenGetter) {
  return api<IssuedComplianceRequest>(
    `/api/compliance/requests/${requestPublicId}/resend`,
    { method: 'POST', tokenGetter, body: '{}' },
  );
}

export function revokeComplianceRequest(
  requestPublicId: string, reason: string | null, tokenGetter: TokenGetter,
) {
  return api<{ status: string }>(
    `/api/compliance/requests/${requestPublicId}/revoke`,
    { method: 'POST', tokenGetter, body: JSON.stringify({ reason }) },
  );
}

export function generateComplianceDocument(publicId: string, tokenGetter: TokenGetter) {
  return api<ComplianceDocumentSummary & { existing: boolean }>(
    `/api/compliance/submissions/${publicId}/document`,
    { method: 'POST', tokenGetter, body: '{}' },
  );
}

/** A SHORT-LIVED signed URL. Never cache it; ask again when it lapses. */
export function getComplianceDocumentUrl(publicId: string, tokenGetter: TokenGetter) {
  return api<{
    url: string; filename: string; sha256: string; byteSize: number; expiresInSec: number;
  }>(`/api/compliance/submissions/${publicId}/document`, { tokenGetter });
}

// ---------------------------------------------------------------------------
// RFQ files (/api/rfqs/:id/documents). Upload is start → PUT to R2 → finalize;
// client/src/lib/rfq-files.ts runs the three steps.
// ---------------------------------------------------------------------------

export type { RfqDocumentKind };

export interface RfqDocument {
  /** `rdoc_…`; null for a file uploaded before the Files panel existed. */
  id: string | null;
  kind: RfqDocumentKind;
  filename: string;
  sizeBytes: number | null;
  sha256: string | null;
  lineItemId: string | null;
  lineNumber: number | null;
  note: string | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
  legacy: null | {
    source: 'line_cert' | 'replaced_line_cert' | 'customer_po' | 'replaced_customer_po' | 'vendor_quote_pdf';
    objectKey: string;
  };
  removedAt: string | null;
  removalReason: string | null;
}

export function listRfqDocuments(rfqPublicId: string, tokenGetter: TokenGetter) {
  return api<{ documents: RfqDocument[] }>(`/api/rfqs/${rfqPublicId}/documents`, { tokenGetter });
}

export function startRfqDocumentUpload(
  rfqPublicId: string,
  body: { fileName: string; sizeBytes: number; kind: RfqDocumentKind; lineItemId?: string | null },
  tokenGetter: TokenGetter,
) {
  return api<{ documentId: string; uploadUrl: string; method: 'PUT'; headers: Record<string, string> }>(
    `/api/rfqs/${rfqPublicId}/documents/uploads`,
    { method: 'POST', tokenGetter, body: JSON.stringify(body) },
  );
}

export function finalizeRfqDocument(rfqPublicId: string, documentId: string, tokenGetter: TokenGetter) {
  return api<{ status: 'ready'; documentId: string }>(
    `/api/rfqs/${rfqPublicId}/documents/${documentId}/finalize`,
    { method: 'POST', tokenGetter, body: '{}' },
  );
}

export function updateRfqDocument(
  rfqPublicId: string,
  documentId: string,
  patch: { kind?: RfqDocumentKind; lineItemId?: string | null; note?: string | null },
  tokenGetter: TokenGetter,
) {
  return api<{ changed: boolean }>(`/api/rfqs/${rfqPublicId}/documents/${documentId}`, {
    method: 'PATCH', tokenGetter, body: JSON.stringify(patch),
  });
}

export function removeRfqDocument(rfqPublicId: string, documentId: string, reason: string, tokenGetter: TokenGetter) {
  return api<{ changed: boolean }>(`/api/rfqs/${rfqPublicId}/documents/${documentId}/remove`, {
    method: 'POST', tokenGetter, body: JSON.stringify({ reason }),
  });
}

export function getRfqDocumentUrl(rfqPublicId: string, documentId: string, download: boolean, tokenGetter: TokenGetter) {
  return api<{ url: string; expiresInSec: number }>(
    `/api/rfqs/${rfqPublicId}/documents/${documentId}/url${download ? '?download=1' : ''}`,
    { tokenGetter },
  );
}

/** GET/PUT /api/billing/ship-from-locations — saved 3PL / warehouse addresses
 *  (shared/ship-from.ts). Used by the vendor PO and commercial invoice forms. */
export interface ListShipFromLocationsResponse {
  locations: ShipFromLocation[];
}
export type { ShipFromLocation };
